import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import { inject, injectable } from 'inversify';
import md5 from 'md5';
import 'reflect-metadata';

import {
  TYPES as ApiGatewayTypes,
  BatchPayload,
  BatchResponse,
  IApiGateway,
} from '@desygner/ui-core-api-gateway';

import { DESYGNER_TEAM_ID, DesygnerUserInfo, Team } from './collaborate.desygner-types';
import { ICollaborateService } from './collaborate.interfaces';
import { Collaborator, InvitationStatus, Project, UserId, UserInfo } from './collaborate.types';
import { CollaborateService as BaseCollaborateService } from './collaborate.webrand-service';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

@injectable()
export class CollaborateService extends BaseCollaborateService implements ICollaborateService {
  private teamId: number | null = null;
  constructor(
    @inject(ApiGatewayTypes.ApiGateway)
    protected override apiGateway: IApiGateway,
    private readonly userToken?: string
  ) {
    super(apiGateway);
  }

  private get desygnerEndpointBase() {
    return `brand/companies`;
  }

  private async getTeamId(): Promise<number | null> {
    if (this.teamId !== null) {
      return this.teamId;
    }

    const { data } = await this.apiGateway.fetch('GET', `${this.desygnerEndpointBase}?owner=true`);

    const teams = data as Team[];
    const id = teams.find((team) => team.domain.startsWith('_'))?.id ?? null;

    this.teamId = id;
    return id;
  }

  protected async getMemberships(): Promise<DesygnerUserInfo[]> {
    if (this.cache.has('memberships')) {
      return this.cache.get('memberships');
    }

    const teamId = await this.getTeamId();

    if (teamId === null) {
      return [];
    }

    const { data: response } = await this.apiGateway.fetch(
      'GET',
      `${this.desygnerEndpointBase}/${teamId}/memberships`
    );
    const memberships = (response as DesygnerUserInfo[]).map<DesygnerUserInfo>((info) => ({
      ...info,
      email: info.details.find((detail) => detail.type === 'email')?.content ?? '',
      name: [
        info.details.find((detail) => detail.type === 'first_name')?.content,
        info.details.find((detail) => detail.type === 'last_name')?.content,
      ]
        .filter(Boolean)
        .join(' '),
      is_expired: !!(
        info.invite_link_expires_at && dayjs(new Date()).isAfter(dayjs(info.invite_link_expires_at))
      ),
    }));

    this.cache.set('memberships', memberships);
    return memberships;
  }

  override async getMembers({ encodedId, projectId }: Project): Promise<Collaborator[]> {
    const cacheKey = `members-${encodedId}-${projectId}`;

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    const [acceptedMembers, memberships] = await Promise.all([
      super.getMembers({ encodedId, projectId }),
      this.getMemberships(),
    ]);

    const pendingMembers = memberships
      .filter(
        (member) =>
          member.status === InvitationStatus.SENT || member.status === InvitationStatus.EXPIRED
      )
      .map<Collaborator>((member) => ({
        name: member.name || member.email || '',
        email: member.email ?? '',
        invitation: {
          membershipId: member.id,
          isPending: true,
          isExpired: member.is_expired,
          invitedAt: member.invite_date,
        },
        permissionId: member.id,
        permissionType: 'shared',
        id: member.email ?? '',
      }));

    const acceptedMembersInTeam = acceptedMembers.filter((member) => {
      const membership = memberships.find(
        (membership) =>
          membership.user?.id === member.id && membership.status === InvitationStatus.ACCEPTED
      );

      if (!membership && memberships.length !== 0) {
        return false;
      }

      member.email = membership?.email ?? member.email;
      member.name = membership?.name ?? membership?.email ?? member.name;
      member.sourceUrl =
        (membership?.user ?? membership)?.details.find((detail) => detail.type === 'profile_url')
          ?.content ?? member.sourceUrl;

      member.invitation = {
        membershipId: membership?.id ?? (member.id as number),
        isPending: false,
        isExpired: membership?.is_expired ?? false,
        invitedAt: membership?.invite_date ?? '',
      };

      return true;
    });

    const invitedMembers = [...acceptedMembersInTeam, ...pendingMembers].sort((a, b) =>
      a.email.localeCompare(b.email)
    );

    this.cache.set(cacheKey, invitedMembers);

    return invitedMembers;
  }

  private async createTeam(): Promise<Team> {
    const payload = {
      parent_companies: [DESYGNER_TEAM_ID],
    };

    const { data } = (await this.apiGateway.fetch(
      'POST',
      `${this.desygnerEndpointBase}`,
      payload
    )) as { data: Team };

    this.teamId = data.id;
    return data;
  }

  override async addMembers(project: Project, users: UserId[]): Promise<BatchResponse[]> {
    let teamId = await this.getTeamId();

    if (!teamId) {
      teamId = (await this.createTeam()).id;
    }

    const memberships = await this.getMemberships();
    const nonMembershipPayload = users
      .map<BatchPayload | undefined>((email, index) => {
        const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.toString());

        if (!isEmailValid) return;

        const membership = memberships?.find((member) => member.email === email);

        if (membership) {
          return;
        }

        const payload = {
          is_admin: false,
          invite_date: dayjs().toISOString(),
          details: [
            {
              content: email,
              type: 'email',
            },
            {
              content: memberships[0]?.locale ?? '',
              type: 'language_code',
            },
          ],
          email_hash: md5((this.userToken ?? '') + email).toString(),
        };

        return {
          id: index,
          body: JSON.stringify(payload),
          method: 'post',
          url: this.desygnerEndpointBase + `/${teamId}/memberships`,
        };
      })
      .filter((payload): payload is BatchPayload => !!payload);

    const acceptedMembership = users
      .map((id) => {
        const membership = memberships?.find((member) => member.email === id);

        if (!membership || membership.status !== InvitationStatus.ACCEPTED) return;

        return membership.user?.id;
      })
      .filter((id): id is number => !!id);

    const requests: Promise<BatchResponse[]>[] = [];

    if (nonMembershipPayload.length !== 0) {
      requests.push(this.apiGateway.batch([...nonMembershipPayload]));
    }

    if (acceptedMembership.length !== 0) {
      requests.push(super.addMembers(project, acceptedMembership));
    }

    if (requests.length === 0) {
      return [];
    }

    const result = await Promise.all(requests);

    this.cache.clear();
    return result.flat();
  }

  override async searchMembers(
    keyword: string,
    status: number,
    limit: number,
    offset: number
  ): Promise<UserInfo[]> {
    const memberships = await this.getMemberships();
    const matchedMemberships =
      memberships.filter(
        (member) =>
          keyword === '' ||
          member.email.includes(keyword) ||
          (member.name && member.name?.includes(keyword))
      ) ?? [];

    return matchedMemberships.slice(offset, limit).map<UserInfo>((member) => ({
      id: member.id,
      user: {
        id: member.id,
        uuid: member.email,
        nickname: member.user?.nickname ?? member.name ?? member.email,
        profilePicture: member.user?.profile_picture ?? '',
        details: [{ content: member.email, type: 'email' }],
      },
      invitation: {
        membershipId: member.id,
        isPending: member.status === InvitationStatus.SENT,
        isExpired: member.is_expired,
        invitedAt: member.invite_date,
      },
      details: [{ content: member.email, type: 'email' }],
    }));
  }

  async resendInvitation({
    project,
    membershipId,
  }: {
    project: Project;
    membershipId: number;
  }): Promise<void> {
    if (!project || !membershipId) {
      return;
    }

    const teamId = await this.getTeamId();
    const memberships = await this.getMemberships();
    const membership = memberships.find((member) => member.id === membershipId);

    if (!membership) {
      return;
    }

    const payload = {
      ...membership,
      invite_date: dayjs().utc().toISOString(),
    };

    await this.apiGateway.fetch(
      'PUT',
      `${this.desygnerEndpointBase}/${teamId}/memberships/${membershipId}`,
      payload
    );
  }

  async getMembershipById(id: DesygnerUserInfo['id']): Promise<DesygnerUserInfo | null> {
    const memberships = await this.getMemberships();

    return memberships.find((member) => member.id === id) ?? null;
  }

  async removeWorkspaceMember(collaborator: Collaborator): Promise<void> {
    const membership = await this.getMembershipById(collaborator.invitation?.membershipId ?? 0);

    if (!membership?.id) {
      return;
    }

    const teamId = await this.getTeamId();

    await this.apiGateway.fetch(
      'DELETE',
      `${this.desygnerEndpointBase}/${teamId}/memberships/${membership.id}`,
      {}
    );

    this.cache.clear();
    return;
  }

  /**
   * If User is registered, It uses Desygner API
   * If User is not registered, It uses Fluer API
   */
  override async removeMembers(project: Project, users: Collaborator[]): Promise<void> {
    if (users.length === 0) return;

    const pendingUsers = users.filter(
      (user) => user.invitation?.isPending || user.invitation?.isExpired
    );
    const acceptedUsers = users.filter(
      (user) => !user.invitation?.isPending && !user.invitation?.isExpired
    );
    const removeInvites = pendingUsers.map((user) => this.removeWorkspaceMember(user));

    await Promise.all([super.removeMembers(project, acceptedUsers), ...removeInvites]);

    this.cache.clear();
  }
}
